<template>
  <div>
    <div v-if="featured" class="background--black featured-mb">
      <div class="featured-article featured-article--no-margin container">
        <div class="featured-article__image">
          <img
            :src="featuredArticle.homePageImage"
            :alt="featuredArticle.title"
          />
        </div>
        <div class="featured-article-content">
          <div
            v-if="
              featuredArticle.categories &&
              featuredArticle.categories.length > 0
            "
            class="featured-article__label"
          >
            {{ $t(featuredArticle.categories[0].name).toUpperCase() }}
          </div>
          <h1 class="featured-article__title">{{ featuredArticleTitle }}</h1>
          <p
            class="featured-article__excerpt"
            v-html="featuredArticleExcerpt"
          />
          <div class="featured-article__links">
            <a
              class="featured-article__link--focus"
              :href="featuredArticleUrl"
            >
              {{ $t("buttons.read-article").toUpperCase() }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="background--light-gray background--light-gray--no-overflow">
      <div class="article-index container">
        <div class="article-index__wrapper">
          <transition-group name="list" class="article-index__list">
            <ArticleBlockComponent
              v-for="article in articles"
              :key="article.node.slug"
              :article="article.node"
              :action="action"
              :featured="featured"
              :show-category="featured"
              :fixed-style="featured ? 'third' : 'list-item'"
            />
          </transition-group>
          <a
            v-if="hasNextPage"
            :class="[
              {
                'full-width': !$isDesktop || !showConnect,
              },
              'button button--block button--load-more',
            ]"
            role="button"
            @click="loadMoreArticles"
          >
            <div v-if="isLoading" class="spinner-border" role="status">
              <span class="sr-only" />
            </div>
            <div class="load-more-content" v-else>View more Articles</div>
          </a>
        </div>

        <ConnectComponent
          v-if="showConnect"
          :locale="locale"
          :country="country"
          :country-newsletter-id="countryNewsletterId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import ArticleBlockComponent from "./ArticleBlockComponent.vue";
import ConnectComponent from "../connect/ConnectComponent.vue";

export default {
  components: {
    ArticleBlockComponent,
    ConnectComponent,
  },
  props: {
    action: {
      type: String,
      default: undefined,
    },
    featured: {
      type: Boolean,
      default: false,
    },
    initialArticles: {
      type: Object,
      default: () => ({
        edges: [],
        pageInfo: {
          endCursor: null,
          hasNextPage: true,
        },
      }),
    },
    authorId: {
      type: Number,
      default: null,
    },
    designer: {
      type: String,
      default: null,
    },
    photographer: {
      type: String,
      default: null,
    },
    categoryName: {
      type: String,
      default: 'articles',
    },
    locale: {
      type: String,
      default: 'en',
    },
    country: {
      type: String,
      default: 'SG',
    },
    showConnect: {
      type: Boolean,
      default: true,
    },
    categorySlug: {
      type: String,
      default: null,
    },
    countryNewsletterId: {
      type: String,
      default: 'c04c8e2b7e',
    },
  },
  apollo: {
    articles: {
      query: gql`
        query getArticles(
          $locale: String!
          $country: String!
          $cursor: String
          $authorId: ID
          $photographer: ID
          $designer: ID
          $category_slug: String
        ) {
          articles(
            first: 10
            after: $cursor
            locale: $locale
            country: $country
            authorId: $authorId
            photographerId: $photographer
            designerId: $designer
            type: $category_slug
          ) {
            edges {
              node {
                image
                homePageImage
                title
                titleInCountry(country: $country)
                languageInCountry(country: $country)
                slug
                url
                spins
                genre
                isPaid
                categories {
                  name
                  slug
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      `,
      loadingKey: "isLoading",
      variables() {
        return {
          authorId: this.authorId,
          photographer: this.photographer,
          designer: this.designer,
          locale: this.locale,
          country: this.country,
          cursor: this.cursor,
          category_name: this.categoryName,
          category_slug: this.categorySlug,
        };
      },
      update(data) {
        this.hasNextPage = data.articles.pageInfo.hasNextPage;

        return [...this.articles, ...data.articles.edges];
      },
      result({ loading, data }) {
        this.skipQuery = true;
        this.isLoading = false;
        if (!loading) {
          this.cursor = data.articles.pageInfo.endCursor;
        }
      },
      skip() {
        return this.skipQuery;
      },
    },
  },
  data() {
    return {
      isLoading: false,
      skipQuery: true,
      hasNextPage: this.initialArticles.pageInfo.hasNextPage,
      articles: this.featured
        ? this.initialArticles.edges.slice(1)
        : this.initialArticles.edges,
      cursor: this.initialArticles.pageInfo.endCursor,
    };
  },
  computed: {
    featuredArticle() {
      return this.initialArticles.edges[0].node;
    },

    featuredArticleUrl() {
      const language = this.featuredArticle.languageInCountry;

      return `${this.featuredArticle.url}${language ? `/${language}` : ''}`;
    },

    featuredArticleTitle() {
      return this.featuredArticle.titleInCountry || this.featuredArticle.title;
    },

    featuredArticleExcerpt() {
      return this.featuredArticle.excerptInCountry || this.featuredArticle.excerpt;
    },
  },
  methods: {
    loadMoreArticles() {
      this.skipQuery = false;
      this.isLoading = true;
      // uses refetch instead of fetchMore as we are skipping the initialQuery.
      this.$apollo.queries.articles.refetch();
    },
  },
};
</script>
